import { Suspense, lazy, useEffect, useState, useRef, useCallback, useMemo } from "react";
import SectionGrid from "VFCMF/SectionGrid";
import SectionScroll from "VFCMF/SectionScroll";
import SectionPromo from "VFCMF/SectionPromo";
import SectionItemVideo from "VFCMF/SectionItemVideo";
import Player from "./player/player-static";
import Loader from "./components/loader";
import WidgetI18nProvider from "./providers/I18nProvider";
import { API_URL } from "./utils/consts";

const VIDEO_SECTION_TYPE = "videoSection";
const BLAZE_WIDGET_TYPE = "blaze";
const SECTION_COMPONENTS = {
  grid: SectionGrid,
  promo: SectionPromo,
  scroll: SectionScroll,
  default: SectionGrid,
};

const Widget = ({ token, apiurl, viewercountry, requireauth, labels, searchtext, orderby, refreshkey }) => {
  const widgetRef = useRef(null);
  const styleElementRef = useRef(null);
  const [widgetState, setWidgetState] = useState({
    data: {},
    untransformedData: {},
    loading: true,
    error: "",
    Component: null,
    type: null,
  });

  const [playerState, setPlayerState] = useState({
    isOpen: false,
    currentItem: null,
  });

  const normalizedRequireAuth = requireauth === true || requireauth === 'true';
  const normalizedViewerCountry = viewercountry || '';

  // Event dispatcher for Blaze widget events
  const dispatchWidgetEvent = useCallback((eventType, eventData) => {
    if (widgetRef?.current) {
      const customEvent = new CustomEvent(`widget-${eventType}`, {
        detail: {
          ...eventData,
          widgetToken: token,
          timestamp: Date.now(),
        },
        bubbles: true,
        cancelable: true,
      });
      widgetRef.current.dispatchEvent(customEvent);
    }
  }, [token]);

  const eventHandlers = useMemo(() => ({
    onError: (error) => dispatchWidgetEvent('data-load-error', error),
    onDataLoadCompleted: (eventData) => dispatchWidgetEvent('data-loaded', eventData),
    onWidgetItemClick: (itemData) => dispatchWidgetEvent('item-click', itemData),
  }), [dispatchWidgetEvent]);

  const handleClosePlayer = () => {
    setPlayerState({
      isOpen: false,
      currentItem: null,
    });
  };

  const apiUrl = apiurl || API_URL;

  const widgetDataToSectionData = useCallback((data) => {
    if (data.type === VIDEO_SECTION_TYPE) {
      return {
        description: "",
        items: {
          data:
            data?.content?.map(({ id, src, ...rest }) => ({
              input: { id, src, ...rest },
              token: id,
              url: src,
            })) || [],
        },
        SectionItem: SectionItemVideo,
        layout: data.settings.layout,
        settings: data.settings.common,
        name: data.name,
        theme: {
          ...data.settings.common,
          ...data.defaultStyle.section,
          itemCount: data?.content?.length || 0,
          /*
        ADDITIONAL PARAMS:
            link : {enabled: true, url : "YOUR URL"}, //For Asset section
            src: "[ASSET URL]", //For Asset section
            css: {}, //For HTML sections
            code: "<h1>Some HTML</h1>", //For HTML and Markdown sections
        */
        },
        token: data.id,
        type: data.settings.type,
        onItemClick: (itemData, clickEvent) => {
          clickEvent.preventDefault();
          setPlayerState({
            isOpen: true,
            currentItem: itemData,
          });
        },
        apiUrl,
        /*
        ADDITIONAL PARAMS:
        itemURLFormat : (sectionType, item, url) => {//Your URL logic here} //Optional - For additional/custom url formatting
        */
      };
    }
    return {
      data,
      apiUrl,
      eventHandlers,
    };
  }, [apiUrl, eventHandlers]);

  const getComponent = (type, layout) => {
    if (type === VIDEO_SECTION_TYPE) {
      return SECTION_COMPONENTS[layout] || SECTION_COMPONENTS.default;
    }
    
    if (type === BLAZE_WIDGET_TYPE) {
      return lazy(() => import('./widgets/blaze/blaze.tsx'));
    }
    
    return lazy(() => import(`./widgets/${type}/${type}.jsx`));
  };

  // Fetch and process widget config
  useEffect(() => {
    const fetchWidgetConfig = async () => {
      try {
        const url = `${apiUrl}/widgets/${token}`;
        
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`${response.status}: ${response.statusText}`);
        }

        const resData = await response.json();
        
        // Transform and set widget state
        const transformedData = widgetDataToSectionData(resData);
        const component = getComponent(resData.type, resData.settings?.layout);

        if (resData.style && resData.type !== VIDEO_SECTION_TYPE) {
          if (styleElementRef.current) {
            styleElementRef.current.remove();
          }
          const styleEl = document.createElement("style");
          styleEl.type = "text/css";
          styleEl.appendChild(document.createTextNode(resData.style));
          document.head.appendChild(styleEl);
          styleElementRef.current = styleEl;
        }

        setWidgetState({
          data: transformedData,
          loading: false,
          error: "",
          Component: component,
          type: resData.type,
        });
      } catch (error) {
        console.error("Widget fetch error:", error);
        setWidgetState((state) => ({
          ...state,
          loading: false,
          error: "Content is not available",
        }));
        eventHandlers.onError("Content is not available");
      }
    };  

    // refreshkey is an intentional trigger dep — changing it forces a re-fetch (live update)
    fetchWidgetConfig();

    return () => {
      if (styleElementRef.current) {
        styleElementRef.current.remove();
        styleElementRef.current = null;
      }
    };
  }, [token, apiUrl, eventHandlers, widgetDataToSectionData, refreshkey]);

  const { data, loading, error, Component, type } = widgetState;

  if (error) return null;

  return (
    <div ref={widgetRef}>
        <Suspense fallback={<Loader />}>
          {Component &&
            (type === VIDEO_SECTION_TYPE ? (
              <WidgetI18nProvider>
                <Component {...data} />
                {playerState.isOpen && playerState.currentItem && (
                  <Player
                    item={playerState.currentItem.input}
                    widgetToken={data.token}
                    settings={data.settings}
                    onClose={handleClosePlayer}
                    apiUrl={apiUrl}
                  />
                )}
              </WidgetI18nProvider>
            ) : (
              <Component 
                data={data.data} 
                apiUrl={data.apiUrl} 
                viewerCountry={normalizedViewerCountry}
                requireAuth={normalizedRequireAuth}
                eventHandlers={data.eventHandlers}
                labels={labels}
                searchText={searchtext}
                orderBy={orderby}
              />
            ))}
        </Suspense>
    </div>
  );
};

export default Widget;
